import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Lock, Loader2, RefreshCcw, LogOut } from "lucide-react";
import { adminLogin, adminLeads, adminStats } from "@/lib/api";
import { toast } from "sonner";

export default function Admin() {
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const t = localStorage.getItem("samota_admin");
    if (t) setToken(t);
  }, []);

  useEffect(() => {
    if (!token) return;
    refresh();
    // eslint-disable-next-line
  }, [token]);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([adminLeads(token), adminStats(token)]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Could not load admin data.");
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await adminLogin(password);
      localStorage.setItem("samota_admin", r.token);
      setToken(r.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("samota_admin");
    setToken("");
    setLeads([]);
    setStats(null);
  };

  if (!token) {
    return (
      <div className="bg-slate-50 min-h-screen pt-28 pb-20" data-testid="page-admin-login">
        <div className="mx-auto max-w-md px-4">
          <Card className="p-8 soft-shadow border-slate-100">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-900 text-white">
                <Lock className="h-5 w-5" />
              </span>
              <div>
                <h1 className="font-display text-xl font-semibold text-slate-900">Admin sign-in</h1>
                <p className="text-xs text-slate-500">Samota Insurance dashboard</p>
              </div>
            </div>
            <form onSubmit={login} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="admin-pw">Password</Label>
                <Input id="admin-pw" data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" required />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-blue-900 hover:bg-blue-950 text-white" data-testid="admin-login-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen pt-28 pb-20" data-testid="page-admin">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">Admin Dashboard</Badge>
            <h1 className="font-display mt-3 text-3xl font-semibold tracking-tight text-slate-900">Leads & assessments</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refresh} disabled={loading} data-testid="admin-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-1" />}
              Refresh
            </Button>
            <Button variant="outline" onClick={logout} data-testid="admin-logout">
              <LogOut className="h-4 w-4 mr-1" /> Logout
            </Button>
          </div>
        </div>

        {stats && (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { l: "Total leads", v: stats.total_leads },
              { l: "New", v: stats.new_leads },
              { l: "High-score (≥70)", v: stats.high_score_leads },
              { l: "Assessments completed", v: stats.assessments },
            ].map((s) => (
              <Card key={s.l} className="p-5 border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-500">{s.l}</div>
                <div className="font-display text-3xl font-semibold mt-1 text-slate-900">{s.v}</div>
              </Card>
            ))}
          </div>
        )}

        <Card className="mt-8 border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Interest</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                  <th className="text-left px-4 py-3 font-semibold">Score</th>
                  <th className="text-left px-4 py-3 font-semibold">Created</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100" data-testid={`admin-lead-${l.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                    <td className="px-4 py-3"><a href={`tel:${l.phone}`} className="text-blue-900 hover:underline">{l.phone}</a></td>
                    <td className="px-4 py-3 text-slate-600">{l.email || "—"}</td>
                    <td className="px-4 py-3">{l.interest || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{l.source}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        l.score >= 70 ? "bg-emerald-50 text-emerald-700" : l.score >= 40 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"
                      }`}>{l.score}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
    </div>
  );
}
